import React from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Filter, X, Users as UsersIcon } from "lucide-react";
import { PROJECT_STATUSES } from "@/components/EditProjectDialog";

/**
 * Search + status + 5 people filters + "only unassigned" toggle. Purely
 * presentational — every state slice is owned by the parent so filter
 * memoization there sees primitive values (not objects) as inputs.
 */
export default function ProjectFilters({
  search, setSearch,
  statusFilter, setStatusFilter,
  cmFilter, setCmFilter,
  tlFilter, setTlFilter,
  writerFilter, setWriterFilter,
  editorFilter, setEditorFilter,
  publisherFilter, setPublisherFilter,
  filterOptionsByRole,
  userMap,
  anyPeopleFilterActive,
  clearPeopleFilters,
  showOnlyUnassigned, setShowOnlyUnassigned,
  filteredCount, totalCount,
}) {
  const peopleFilters = [
    { key: "cm",        label: "CM",        value: cmFilter,        setter: setCmFilter,        options: filterOptionsByRole.cm },
    { key: "tl",        label: "TL",        value: tlFilter,        setter: setTlFilter,        options: filterOptionsByRole.tl },
    { key: "writer",    label: "Writer",    value: writerFilter,    setter: setWriterFilter,    options: filterOptionsByRole.writer },
    { key: "editor",    label: "Editor",    value: editorFilter,    setter: setEditorFilter,    options: filterOptionsByRole.editor },
    { key: "publisher", label: "Publisher", value: publisherFilter, setter: setPublisherFilter, options: filterOptionsByRole.publisher },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative flex-1 min-w-[240px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search project, assignee, status, URL…"
          className="pl-9"
          data-testid="projects-search"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="h-9 w-40" data-testid="projects-status-filter">
          <div className="flex items-center gap-2"><Filter className="h-3.5 w-3.5 text-slate-500" /><SelectValue /></div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          {PROJECT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {/* People filters — narrow by CM / TL / Writer / Editor / Publisher.
          Each dropdown lists only users who appear in at least one project
          in that role (keeps the menu tight). */}
      {peopleFilters.map(({ key, label, value, setter, options }) => (
        <Select key={key} value={value} onValueChange={setter}>
          <SelectTrigger
            className={`h-9 w-36 ${value !== "any" ? "border-purple-400 bg-purple-50 text-purple-800" : ""}`}
            data-testid={`projects-${key}-filter`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <UsersIcon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <span className="truncate">
                {value === "any" ? label : (userMap[value]?.name || label)}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any {label}</SelectItem>
            {options.length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-slate-400 italic">No {label.toLowerCase()}s assigned</div>
            ) : (
              options.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)
            )}
          </SelectContent>
        </Select>
      ))}
      {anyPeopleFilterActive && (
        <button
          onClick={clearPeopleFilters}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300 rounded-md px-2 py-1.5 bg-white transition"
          data-testid="projects-clear-people-filters"
          title="Clear all people filters"
        >
          <X className="h-3 w-3" /> Clear
        </button>
      )}
      <label className={`inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border cursor-pointer transition ${showOnlyUnassigned ? "bg-red-50 border-red-200 text-red-800" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
        <input type="checkbox" checked={showOnlyUnassigned} onChange={(e) => setShowOnlyUnassigned(e.target.checked)} className="accent-red-600" data-testid="projects-unassigned-toggle" />
        Only unassigned
      </label>
      <div className="ml-auto text-xs text-slate-500">{filteredCount} of {totalCount}</div>
    </div>
  );
}
